// Phase 2 — Form layout primitives (docs/04 §4.2).
//
// Layout only — deliberately form-library-agnostic. Validation rules live in the
// screen (copied verbatim from the legacy antd/MUI forms when each is migrated);
// these components just provide the card/section/field structure and error slot.
import * as React from "react";
import { Card, CardContent } from "ui/card";
import { Label } from "ui/label";
import { cn } from "lib/utils";

export interface FormSectionProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  /** Number of columns on md+ screens (single column on mobile). Default 2. */
  columns?: 1 | 2 | 3;
  className?: string;
}

const COL_CLASS: Record<number, string> = {
  1: "md:grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
};

export function FormSection({ title, description, children, columns = 2, className }: FormSectionProps) {
  return (
    <Card className={cn("shadow-sm", className)}>
      <CardContent className="space-y-4 p-6">
        {(title || description) && (
          <div className="space-y-1">
            {title && <h2 className="text-base font-semibold text-foreground">{title}</h2>}
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
        )}
        <div className={cn("grid grid-cols-1 gap-4", COL_CLASS[columns])}>{children}</div>
      </CardContent>
    </Card>
  );
}

export interface FieldRowProps {
  label: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  /** Inline validation error text (shown in destructive color). */
  error?: React.ReactNode;
  hint?: React.ReactNode;
  children: React.ReactNode;
  /** Span the full grid width. */
  full?: boolean;
  className?: string;
}

export function FieldRow({ label, htmlFor, required, error, hint, children, full, className }: FieldRowProps) {
  return (
    <div className={cn("space-y-1.5", full && "md:col-span-full", className)}>
      <Label htmlFor={htmlFor}>
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
